import { CSSProperties, useState } from "react";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "../../CustomButton";
import SpinnerForBtn from "../../loaders/SpinnerForBtn";
import { useTranslation } from "@/hooks/localization/useTranslations";

interface Props {
  moveTo: (position: google.maps.LatLngLiteral) => void;
}

const btnStyle: CSSProperties = {
  position: "absolute",
  zIndex: 2,
  bottom: 8,
  left: 8,
};

const CurrentLocation: React.FC<Props> = ({ moveTo }) => {
  const t = useTranslation();
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);

  const geolocationOptions: PositionOptions = {
    enableHighAccuracy: true, // Still using high accuracy
    timeout: 5000, // Reduced timeout to 5 seconds for faster failure
    maximumAge: 0, // Don't use cached position
  };

  const getCurrentLocation = () => {
    setDisabled(true);
    setError(null);
    setAccuracy(null);

    if (!navigator.geolocation) {
      setError(t.geolocation_not_supported || "Geolocation is not supported");
      setDisabled(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setDisabled(false);
        const coords = position.coords;
        setAccuracy(coords.accuracy);

        moveTo({
          lat: coords.latitude,
          lng: coords.longitude,
        });
      },
      (err) => {
        setDisabled(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setError(t.location_permission_denied);
            break;
          case err.POSITION_UNAVAILABLE:
            setError(t.location_unavailable);
            break;
          case err.TIMEOUT:
            setError(t.location_timeout);
            break;
          default:
            setError(t.location_unknown_error);
        }
      },
      geolocationOptions,
    );
  };

  return (
    <div style={btnStyle}>
      <CustomButton
        type={BtnTypes.primary}
        size={BtnSizes.medium}
        radius={BtnRadius.small}
        disabled={disabled}
        onClick={getCurrentLocation}
      >
        {disabled ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <SpinnerForBtn />
            {t.locating}
          </div>
        ) : (
          t.my_location
        )}
      </CustomButton>

      {error && (
        <div style={{ color: "red", marginTop: "8px", maxWidth: "200px" }}>
          {error}
        </div>
      )}

      {accuracy && (
        <div style={{ color: "#666", marginTop: "8px", fontSize: "12px" }}>
          {t.accuracy}: ~{Math.round(accuracy)} meters
        </div>
      )}
    </div>
  );
};

export default CurrentLocation;
